import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Alert, Badge, Button, Col, Form, InputGroup, ListGroup, Row, Tab, Tabs } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faTrash, faArrowUp, faArrowDown, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { trackPromise } from 'react-promise-tracker';
import Swal from 'sweetalert2';
import Select from 'react-select';
import caAPI from '../../../../services/caAPI';
import ModalDommus from '../../../../components/Modal';
import DommusRequiredSpan from '../../../../components/DommusRequiredSpan';
import { successToast } from '../../../../components/DommusToast';
import { DommusSortable, DommusSortableItem } from '../../../../components/DommusSortable';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { extrairDados, obterMensagemErro } from '../utils';

function extrairModelosIa(resposta) {
  const dados = extrairDados(resposta);
  if (Array.isArray(dados)) return { opcoes: dados, modeloPadrao: '' };

  return {
    opcoes: Array.isArray(dados?.opcoes) ? dados.opcoes : [],
    modeloPadrao: dados?.modeloPadrao || '',
  };
}

function mapearErros422(erro, setError) {
  const erros = erro?.response?.data || {};
  const mapeado = {};
  Object.keys(erros).forEach(campo => {
    if (campo !== 'erro' && campo !== 'message') {
      const msg = Array.isArray(erros[campo]) ? erros[campo][0] : erros[campo];
      mapeado[campo] = msg;
      if (setError) setError(campo, { message: msg });
    }
  });
  return mapeado;
}

function ehModeloGpt5(modeloIa) {
  return typeof modeloIa === 'string' && modeloIa.indexOf('gpt-5') === 0;
}

export default function ModalAgenteIa({ aberto, close, agente, onSalvo }) {
  const editando = Boolean(agente?.id_agente_ia);
  const [modelos, setModelos] = useState([]);
  const [modelosIa, setModelosIa] = useState([]);
  const [usuariosAtor, setUsuariosAtor] = useState([]);
  const [elegibilidadeIndisponivel, setElegibilidadeIndisponivel] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('dados');
  const [erroGeral, setErroGeral] = useState(null);

  const { control, handleSubmit, register, reset, setError, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      modelo: '',
      nome: '',
      id_usuario: '',
      personalidade: '',
      politicas_empresa: '',
      nome_empresa: '',
      descricao_empresa: '',
      modelo_ia: '',
      temperatura: 0.2,
      reasoning_effort: 'low',
      follow_up_habilitado: false,
      follow_up_intervalo_horas: '',
      follow_up_limite_tentativas: '',
      follow_up_janela_inicio: '08:00',
      follow_up_janela_fim: '20:00',
    },
  });

  const modeloSelecionado = watch('modelo');
  const modeloIaSelecionado = watch('modelo_ia');
  const modeloIaGpt5 = ehModeloGpt5(modeloIaSelecionado);
  const followUpHabilitado = watch('follow_up_habilitado');

  // Reinicializa o form quando o agente muda (abrir edição ou novo)
  useEffect(() => {
    if (!aberto) {
      setAbaAtiva('dados');
      setErroGeral(null);
      return;
    }
    reset({
      modelo: agente?.modelo || '',
      nome: agente?.nome || '',
      id_usuario: agente?.id_usuario || '',
      personalidade: agente?.personalidade || '',
      politicas_empresa: agente?.politicas_empresa || '',
      nome_empresa: agente?.nome_empresa || '',
      descricao_empresa: agente?.descricao_empresa || '',
      modelo_ia: agente?.modelo_ia || '',
      temperatura: agente?.temperatura ?? 0.2,
      reasoning_effort: agente?.reasoning_effort || 'low',
      follow_up_habilitado: Boolean(agente?.follow_up_habilitado),
      follow_up_intervalo_horas: agente?.follow_up_intervalo_horas ?? '',
      follow_up_limite_tentativas: agente?.follow_up_limite_tentativas ?? '',
      follow_up_janela_inicio: (agente?.follow_up_janela_inicio || '08:00:00').slice(0, 5),
      follow_up_janela_fim: (agente?.follow_up_janela_fim || '20:00:00').slice(0, 5),
    });

    trackPromise(
      caAPI.agenteIa.listarModelos()
        .then(resposta => setModelos(extrairDados(resposta)))
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao carregar modelos de Agente.'),
          icon: 'error',
        }))
    );
  }, [aberto, agente, reset]);

  // Carrega modelos de IA quando o modelo de agente muda
  useEffect(() => {
    if (!modeloSelecionado) {
      setModelosIa([]);
      return;
    }
    trackPromise(
      caAPI.agenteIa.listarModelosIa(modeloSelecionado)
        .then(resposta => {
          const { opcoes, modeloPadrao } = extrairModelosIa(resposta);
          setModelosIa(opcoes);
          if (!editando) setValue('modelo_ia', modeloPadrao || opcoes[0] || '');
        })
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao carregar Modelos de IA.'),
          icon: 'error',
        }))
    );
  }, [editando, modeloSelecionado, setValue]);

  // Carrega os usuários-ator elegíveis para o modelo selecionado. A elegibilidade
  // depende do modelo, então recarrega a cada troca e limpa a seleção anterior.
  function carregarUsuariosAtor(modelo) {
    return trackPromise(
      caAPI.agenteIa.listarUsuariosAtorElegiveis(modelo, agente?.id_agente_ia || null)
        .then(resposta => {
          const corpo = resposta?.data?.data ?? resposta?.data ?? [];
          setUsuariosAtor(Array.isArray(corpo) ? corpo : []);
          setElegibilidadeIndisponivel(Boolean(resposta?.data?.elegibilidade_indisponivel));
        })
        .catch(() => setElegibilidadeIndisponivel(true))
    );
  }

  useEffect(() => {
    if (!modeloSelecionado) {
      setUsuariosAtor([]);
      setElegibilidadeIndisponivel(false);
      return;
    }
    setElegibilidadeIndisponivel(false);
    if (!editando) setValue('id_usuario', '');
    carregarUsuariosAtor(modeloSelecionado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editando, modeloSelecionado, agente]);

  function handleSalvar(valores) {
    setErroGeral(null);

    let dados, requisicao;
    try {
      dados = {
        nome: valores.nome,
        id_usuario: Number(valores.id_usuario),
        personalidade: valores.personalidade,
        politicas_empresa: valores.politicas_empresa,
        nome_empresa: valores.nome_empresa,
        descricao_empresa: valores.descricao_empresa,
        modelo_ia: valores.modelo_ia,
      };
      if (modeloIaGpt5) {
        dados.reasoning_effort = valores.reasoning_effort || 'low';
      } else {
        dados.temperatura = Number(valores.temperatura);
      }

      dados.follow_up_habilitado = Boolean(valores.follow_up_habilitado);
      dados.follow_up_intervalo_horas = valores.follow_up_intervalo_horas === '' ? null : Number(valores.follow_up_intervalo_horas);
      dados.follow_up_limite_tentativas = valores.follow_up_limite_tentativas === '' ? null : Number(valores.follow_up_limite_tentativas);
      dados.follow_up_janela_inicio = valores.follow_up_janela_inicio;
      dados.follow_up_janela_fim = valores.follow_up_janela_fim;

      if (!editando) dados.modelo = valores.modelo;

      requisicao = editando
        ? caAPI.agenteIa.update(agente.id_agente_ia, dados)
        : caAPI.agenteIa.create(dados);
    } catch (erro) {
      Swal.fire({ titleText: 'Erro', text: 'Erro inesperado ao preparar dados.', icon: 'error' });
      return;
    }

    return trackPromise(
      requisicao
        .then(resposta => {
          successToast.fire({ text: `Agente ${editando ? 'atualizado' : 'criado'} com sucesso.` });
          onSalvo(extrairDados(resposta));
        })
        .catch(erro => {
          if (erro?.response?.status === 422) {
            mapearErros422(erro, setError);
            const msgGeral = erro?.response?.data?.erro;
            if (msgGeral) setErroGeral(msgGeral);
            return;
          }
          Swal.fire({
            titleText: 'Erro',
            text: obterMensagemErro(erro, `Erro ao ${editando ? 'atualizar' : 'criar'} agente.`),
            icon: 'error',
          });
        })
    );
  }

  function handleCopiarIdChaveApi() {
    const idChaveApi = agente?.id_chave_api;
    if (!idChaveApi || !navigator.clipboard) {
      Swal.fire({ titleText: 'Erro', text: 'Não foi possível copiar o identificador público da chave.', icon: 'error' });
      return;
    }
    navigator.clipboard.writeText(idChaveApi)
      .then(() => successToast.fire({ text: 'Identificador público da chave copiado.' }))
      .catch(() => Swal.fire({ titleText: 'Erro', text: 'Não foi possível copiar o identificador público da chave.', icon: 'error' }));
  }

  const modeloIaDesabilitado = !modeloSelecionado;

  return (
    <ModalDommus
      open={aberto}
      close={close}
      titulo={editando ? 'Editar Agente' : 'Novo Agente'}
      classeBody="bg-light"
    >
      <Form onSubmit={handleSubmit(handleSalvar)}>
        <Tabs activeKey={abaAtiva} onSelect={setAbaAtiva} className="mb-0" style={{ marginBottom: 0 }}>
          <Tab eventKey="dados" title="Dados do Agente">
            <div className="bg-white rounded p-3">
              <Row>
              <Form.Group as={Col} md={6} controlId="modelo-agente">
                <Form.Label>Modelo de Agente <DommusRequiredSpan /></Form.Label>
                {editando ? (
                  <>
                    <Form.Control value={agente?.modelo || ''} readOnly />
                    <Form.Text className="text-muted">O Modelo não pode ser alterado após a criação.</Form.Text>
                  </>
                ) : (
                  <Controller name="modelo" control={control} rules={{ required: 'Selecione o Modelo de Agente.' }} render={({ field }) => (
                    <>
                      <Form.Control as="select" {...field} isInvalid={Boolean(errors.modelo)}>
                        <option value="">Selecione</option>
                        {modelos.map(m => <option key={m.slug} value={m.slug}>{m.nome}</option>)}
                      </Form.Control>
                      {errors.modelo && <Form.Text className="text-danger">{errors.modelo.message}</Form.Text>}
                    </>
                  )} />
                )}
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="modelo-ia">
                <Form.Label>Modelo de IA <DommusRequiredSpan /></Form.Label>
                <Controller name="modelo_ia" control={control} rules={{ required: 'Selecione o Modelo de IA.' }} render={({ field }) => (
                  <>
                    <Form.Control as="select" {...field} disabled={modeloIaDesabilitado} isInvalid={Boolean(errors.modelo_ia)}>
                      <option value="">Selecione</option>
                      {modelosIa.map(m => <option key={m} value={m}>{m}</option>)}
                    </Form.Control>
                    {modeloIaDesabilitado && <Form.Text className="text-muted">Selecione o Modelo de Agente para liberar esta opção.</Form.Text>}
                    {errors.modelo_ia && <Form.Text className="text-danger">{errors.modelo_ia.message}</Form.Text>}
                  </>
                )} />
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} md={8} controlId="nome-agente">
                <Form.Label>Nome <DommusRequiredSpan /></Form.Label>
                <Form.Control {...register('nome', { required: 'Informe o nome.', maxLength: { value: 100, message: 'Informe no máximo 100 caracteres.' } })} isInvalid={Boolean(errors.nome)} />
                {errors.nome && <Form.Text className="text-danger">{errors.nome.message}</Form.Text>}
              </Form.Group>
              {modeloIaGpt5 ? (
                <Form.Group as={Col} md={4} controlId="reasoning-effort-agente">
                  <Form.Label>Esforço de raciocínio <DommusRequiredSpan /></Form.Label>
                  <Form.Control as="select" {...register('reasoning_effort', { required: 'Selecione o esforço de raciocínio.' })} isInvalid={Boolean(errors.reasoning_effort)}>
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                  </Form.Control>
                  {errors.reasoning_effort && <Form.Text className="text-danger">{errors.reasoning_effort.message}</Form.Text>}
                </Form.Group>
              ) : (
                <Form.Group as={Col} md={4} controlId="temperatura-agente">
                  <Form.Label>Temperatura <DommusRequiredSpan /></Form.Label>
                  <Form.Control type="number" min="0" max="2" step="0.1"
                    {...register('temperatura', {
                      required: 'Informe a temperatura.',
                      min: { value: 0, message: 'Informe um valor entre 0 e 2.' },
                      max: { value: 2, message: 'Informe um valor entre 0 e 2.' },
                      validate: v => Number.isInteger(Number(v) * 10) || 'Use incrementos de 0,1.',
                    })}
                    isInvalid={Boolean(errors.temperatura)}
                  />
                  {errors.temperatura && <Form.Text className="text-danger">{errors.temperatura.message}</Form.Text>}
                </Form.Group>
              )}
            </Row>
            <Row>
              <Form.Group as={Col} controlId="usuario-ator">
                <Form.Label>Usuário-ator <DommusRequiredSpan /></Form.Label>
                {elegibilidadeIndisponivel ? (
                  <Alert variant="warning" className="mb-0">
                    Não foi possível carregar os usuários elegíveis no momento.
                    <Button variant="link" className="p-0 ml-2 align-baseline" type="button" onClick={() => {
                      setElegibilidadeIndisponivel(false);
                      carregarUsuariosAtor(modeloSelecionado);
                    }}>
                      Tentar novamente
                    </Button>
                  </Alert>
                ) : (
                  <Controller name="id_usuario" control={control} rules={{ required: 'Selecione o Usuário-ator.' }} render={({ field }) => {
                    const opcoesUsuariosAtor = usuariosAtor.map(usuario => ({
                      value: String(usuario.id),
                      label: usuario.nome,
                      isDisabled: usuario.elegivel === false,
                    }));
                    return (
                      <>
                        <Select
                          inputId="usuario-ator"
                          options={opcoesUsuariosAtor}
                          value={opcoesUsuariosAtor.find(opcao => opcao.value === String(field.value)) || null}
                          onChange={opcao => field.onChange(opcao?.value || '')}
                          onBlur={field.onBlur}
                          isOptionDisabled={opcao => opcao.isDisabled}
                          isSearchable
                          placeholder="Selecione"
                          noOptionsMessage={() => 'Nenhum usuário encontrado.'}
                          aria-invalid={Boolean(errors.id_usuario)}
                          aria-describedby={errors.id_usuario ? 'erro-usuario-ator' : undefined}
                        />
                        {errors.id_usuario && <Form.Text id="erro-usuario-ator" className="text-danger">{errors.id_usuario.message}</Form.Text>}
                      </>
                    );
                  }} />
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} controlId="personalidade-agente">
                <Form.Label>Personalidade <DommusRequiredSpan /></Form.Label>
                <Form.Control as="textarea" rows={3} {...register('personalidade', { required: 'Informe a personalidade.', maxLength: { value: 1000, message: 'Informe no máximo 1000 caracteres.' } })} isInvalid={Boolean(errors.personalidade)} />
                {errors.personalidade && <Form.Text className="text-danger">{errors.personalidade.message}</Form.Text>}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} controlId="politicas-empresa-agente">
                <Form.Label>Políticas da Empresa</Form.Label>
                <Form.Control as="textarea" rows={4} maxLength={4000} {...register('politicas_empresa', { maxLength: { value: 4000, message: 'Informe no máximo 4000 caracteres.' } })} isInvalid={Boolean(errors.politicas_empresa)} />
                <Form.Text className="text-muted">Máximo de 4000 caracteres.</Form.Text>
                {errors.politicas_empresa && <Form.Text className="text-danger">{errors.politicas_empresa.message}</Form.Text>}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} controlId="nome-empresa-agente">
                <Form.Label>Nome da empresa <DommusRequiredSpan /></Form.Label>
                <Form.Control {...register('nome_empresa', { required: 'Informe o nome da empresa.', maxLength: { value: 200, message: 'Informe no máximo 200 caracteres.' } })} isInvalid={Boolean(errors.nome_empresa)} />
                {errors.nome_empresa && <Form.Text className="text-danger">{errors.nome_empresa.message}</Form.Text>}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} controlId="descricao-empresa-agente">
                <Form.Label>Descrição da empresa <DommusRequiredSpan /></Form.Label>
                <Form.Control as="textarea" rows={4} {...register('descricao_empresa', { required: 'Informe a descrição da empresa.', maxLength: { value: 2000, message: 'Informe no máximo 2000 caracteres.' } })} isInvalid={Boolean(errors.descricao_empresa)} />
                {errors.descricao_empresa && <Form.Text className="text-danger">{errors.descricao_empresa.message}</Form.Text>}
              </Form.Group>
            </Row>
            <hr />
            <h6 className="mb-2">Follow-up automático (WhatsApp)</h6>
            <Row>
              <Form.Group as={Col} md={12} controlId="follow-up-habilitado-agente">
                <Form.Check
                  type="switch"
                  id="follow-up-habilitado-agente"
                  label="Habilitar follow-up automático quando o cliente para de responder"
                  {...register('follow_up_habilitado')}
                />
                <Form.Text className="text-muted">
                  Somente WhatsApp. O follow-up cutuca o agente um número limitado de vezes dentro da janela de 24 horas.
                </Form.Text>
              </Form.Group>
            </Row>
            {followUpHabilitado && (
              <>
                <Row>
                  <Form.Group as={Col} md={4} controlId="follow-up-intervalo-agente">
                    <Form.Label>Intervalo entre tentativas (horas) <DommusRequiredSpan /></Form.Label>
                    <Form.Control type="number" min="1" max="24" step="1"
                      {...register('follow_up_intervalo_horas', {
                        required: 'Informe o intervalo em horas.',
                        min: { value: 1, message: 'Informe no mínimo 1 hora.' },
                        max: { value: 24, message: 'O intervalo não pode exceder 24 horas.' },
                      })}
                      isInvalid={Boolean(errors.follow_up_intervalo_horas)}
                    />
                    {errors.follow_up_intervalo_horas && <Form.Text className="text-danger">{errors.follow_up_intervalo_horas.message}</Form.Text>}
                  </Form.Group>
                  <Form.Group as={Col} md={4} controlId="follow-up-limite-agente">
                    <Form.Label>Limite de tentativas <DommusRequiredSpan /></Form.Label>
                    <Form.Control type="number" min="1" step="1"
                      {...register('follow_up_limite_tentativas', {
                        required: 'Informe o limite de tentativas.',
                        min: { value: 1, message: 'Informe no mínimo 1 tentativa.' },
                      })}
                      isInvalid={Boolean(errors.follow_up_limite_tentativas)}
                    />
                    {errors.follow_up_limite_tentativas && <Form.Text className="text-danger">{errors.follow_up_limite_tentativas.message}</Form.Text>}
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group as={Col} md={4} controlId="follow-up-janela-inicio-agente">
                    <Form.Label>Início da janela de horário</Form.Label>
                    <Form.Control type="time" {...register('follow_up_janela_inicio')} isInvalid={Boolean(errors.follow_up_janela_inicio)} />
                    {errors.follow_up_janela_inicio && <Form.Text className="text-danger">{errors.follow_up_janela_inicio.message}</Form.Text>}
                  </Form.Group>
                  <Form.Group as={Col} md={4} controlId="follow-up-janela-fim-agente">
                    <Form.Label>Fim da janela de horário</Form.Label>
                    <Form.Control type="time" {...register('follow_up_janela_fim')} isInvalid={Boolean(errors.follow_up_janela_fim)} />
                    {errors.follow_up_janela_fim && <Form.Text className="text-danger">{errors.follow_up_janela_fim.message}</Form.Text>}
                  </Form.Group>
                </Row>
                <Form.Text className="text-muted">
                  O produto de intervalo × tentativas precisa caber na janela de 24 horas do WhatsApp. O horário é interpretado no fuso de São Paulo.
                </Form.Text>
              </>
            )}
            {editando && (
              <Row>
                <Form.Group as={Col} controlId="id-chave-api-agente">
                  <Form.Label>Identificador público da chave</Form.Label>
                  <InputGroup>
                    <Form.Control value={agente?.id_chave_api || ''} readOnly />
                    <InputGroup.Append>
                      <Button type="button" variant="outline-secondary" onClick={handleCopiarIdChaveApi}>Copiar</Button>
                    </InputGroup.Append>
                  </InputGroup>
                </Form.Group>
              </Row>
            )}
            {erroGeral && <Alert variant="danger">{erroGeral}</Alert>}
              <div className="d-flex justify-content-end">
                <Button type="submit" variant="success" disabled={isSubmitting}>Salvar</Button>
              </div>
            </div>
          </Tab>
          {editando && (
            <Tab eventKey="capacidades" title="Capacidades">
              <AbaCapacidades idAgenteIa={agente?.id_agente_ia} modeloSlug={agente?.modelo} ativa={abaAtiva === 'capacidades'} />
            </Tab>
          )}
          {editando && (
            <Tab eventKey="bases" title="Bases de Conhecimento">
              <AbaBasesConhecimento idAgenteIa={agente?.id_agente_ia} ativa={abaAtiva === 'bases'} />
            </Tab>
          )}
          {editando && (
            <Tab eventKey="contatos" title="Contatos de Origem">
              <AbaContatosOrigem idAgenteIa={agente?.id_agente_ia} ativoAgente={agente?.ativo} ativa={abaAtiva === 'contatos'} />
            </Tab>
          )}
          {editando && (
            <Tab eventKey="fluxo" title="Fluxo de Atendimento">
              <AbaFluxoAtendimento idAgenteIa={agente?.id_agente_ia} ativa={abaAtiva === 'fluxo'} />
            </Tab>
          )}
        </Tabs>
      </Form>
    </ModalDommus>
  );
}

// ─── Aba Capacidades ────────────────────────────────────────────────────────

function AbaCapacidades({ idAgenteIa, modeloSlug, ativa }) {
  const [capacidades, setCapacidades] = useState([]);
  const [opcoes, setOpcoes] = useState([]);
  const [capacidadesSelecionadas, setCapacidadesSelecionadas] = useState([]);
  const [carregado, setCarregado] = useState(false);

  // Reseta ao trocar de agente sem fechar o modal
  useEffect(() => { setCarregado(false); }, [idAgenteIa]);

  useEffect(() => {
    if (!ativa || !idAgenteIa || carregado) return;
    trackPromise(
      Promise.all([
        caAPI.agenteIa.listarCapacidades(idAgenteIa),
        modeloSlug ? caAPI.agenteIa.listarCapacidadesModelo(modeloSlug) : Promise.resolve({ data: [] }),
      ])
        .then(([resAssoc, resCat]) => {
          const assoc = extrairDados(resAssoc);
          const catalogo = extrairDados(resCat);
          setCapacidades(Array.isArray(assoc) ? assoc : []);
          setOpcoes(Array.isArray(catalogo) ? catalogo : []);
          setCarregado(true);
        })
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao carregar capacidades.'),
          icon: 'error',
        }))
    );
  }, [ativa, idAgenteIa, modeloSlug, carregado]);

  const slugsAssociados = capacidades.map(c => c.slug_capacidade || c.slug);
  const opcoesDisponiveis = opcoes.filter(o => !slugsAssociados.includes(o.slug));

  async function handleAdicionar() {
    for (const capacidade of capacidadesSelecionadas) {
      try {
        const resposta = await caAPI.agenteIa.associarCapacidade(idAgenteIa, capacidade.value);
        setCapacidades(atuais => [...atuais, extrairDados(resposta)]);
      } catch (erro) {
        Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao adicionar capacidade.'),
          icon: 'error',
        });
      }
    }
    setCapacidadesSelecionadas([]);
  }

  function handleRemover(slug) {
    let requisicao;
    try {
      requisicao = caAPI.agenteIa.desassociarCapacidade(idAgenteIa, slug);
    } catch (erro) {
      Swal.fire({ titleText: 'Erro', text: 'Erro inesperado ao remover capacidade.', icon: 'error' });
      return;
    }
    const promise = requisicao
      .then(() => setCapacidades(atuais => atuais.filter(capacidade => (capacidade.slug_capacidade || capacidade.slug) !== slug)))
      .catch(erro => Swal.fire({
        titleText: 'Erro',
        text: obterMensagemErro(erro, 'Erro ao remover capacidade.'),
        icon: 'error',
      }));
    trackPromise(promise);
  }

  return (
    <div className="bg-white rounded p-3">
      {opcoesDisponiveis.length > 0 && (
        <Form.Group controlId="nova-capacidade">
          <Form.Label>Adicionar capacidade</Form.Label>
          <div className="d-flex">
            <Select
              className="flex-grow-1 mr-2"
              inputId="nova-capacidade"
              options={opcoesDisponiveis.map(opcao => ({ value: opcao.slug, label: opcao.nome }))}
              value={capacidadesSelecionadas}
              onChange={opcoesSelecionadas => setCapacidadesSelecionadas(opcoesSelecionadas || [])}
              isMulti
              closeMenuOnSelect={false}
              isSearchable
              placeholder="Selecione"
              noOptionsMessage={() => 'Nenhuma capacidade encontrada.'}
            />
            <Button type="button" variant="success" aria-label="Adicionar capacidades" onClick={() => {
              if (capacidadesSelecionadas.length > 0) trackPromise(handleAdicionar());
            }}>
              <FontAwesomeIcon icon={faPlusCircle} />
            </Button>
          </div>
        </Form.Group>
      )}
      {capacidades.length === 0 && <p className="text-muted">Nenhuma capacidade associada.</p>}
      <ListGroup className="mb-3">
        {capacidades.map(cap => {
          const slug = cap.slug_capacidade || cap.slug;
          const nome = cap.nome || slug;
          const disponivel = cap.disponivel !== false;
          return (
            <ListGroup.Item key={slug} className="d-flex justify-content-between align-items-center">
              <span>
                {nome}
                {!disponivel && (
                  <Badge variant="warning" className="ml-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />indisponível
                  </Badge>
                )}
              </span>
              <Button size="sm" variant="outline-danger" type="button" onClick={() => handleRemover(slug)}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    </div>
  );
}

// ─── Aba Bases de Conhecimento ───────────────────────────────────────────────

function AbaBasesConhecimento({ idAgenteIa, ativa }) {
  const [bases, setBases] = useState([]);
  const [elegiveis, setElegiveis] = useState([]);
  const [baseSelecionada, setBaseSelecionada] = useState(null);
  const [carregado, setCarregado] = useState(false);

  // Reseta ao trocar de agente sem fechar o modal
  useEffect(() => { setCarregado(false); }, [idAgenteIa]);

  useEffect(() => {
    if (!ativa || !idAgenteIa || carregado) return;
    trackPromise(
      Promise.all([
        caAPI.agenteIa.listarBasesConhecimento(idAgenteIa),
        caAPI.agenteIa.listarBasesElegiveis(),
      ])
        .then(([resAssoc, resEleg]) => {
          setBases(extrairDados(resAssoc) || []);
          setElegiveis(extrairDados(resEleg) || []);
          setCarregado(true);
        })
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao carregar bases de conhecimento.'),
          icon: 'error',
        }))
    );
  }, [ativa, idAgenteIa, carregado]);

  const idsAssociados = bases.map(b => b.id_base_conhecimento);
  const opcoesDisponiveis = elegiveis.filter(b => !idsAssociados.includes(b.id_base_conhecimento));

  function handleAdicionar(idBase) {
    let requisicao;
    try {
      requisicao = caAPI.agenteIa.associarBaseConhecimento(idAgenteIa, Number(idBase));
    } catch (erro) {
      Swal.fire({ titleText: 'Erro', text: 'Erro inesperado ao associar base de conhecimento.', icon: 'error' });
      return;
    }
    const promise = requisicao
      .then(resposta => {
        setBases(atuais => [...atuais, extrairDados(resposta)]);
        setBaseSelecionada(null);
      })
      .catch(erro => Swal.fire({
        titleText: 'Erro',
        text: obterMensagemErro(erro, 'Erro ao associar base de conhecimento.'),
        icon: 'error',
      }));
    trackPromise(promise);
  }

  function handleRemover(idBase) {
    let requisicao;
    try {
      requisicao = caAPI.agenteIa.desassociarBaseConhecimento(idAgenteIa, idBase);
    } catch (erro) {
      Swal.fire({ titleText: 'Erro', text: 'Erro inesperado ao remover base de conhecimento.', icon: 'error' });
      return;
    }
    const promise = requisicao
      .then(() => setBases(atuais => atuais.filter(base => base.id_base_conhecimento !== idBase)))
      .catch(erro => Swal.fire({
        titleText: 'Erro',
        text: obterMensagemErro(erro, 'Erro ao remover base de conhecimento.'),
        icon: 'error',
      }));
    trackPromise(promise);
  }

  function handleAlternarVinculo(base) {
    const ativo = !(base.vinculo_ativo !== false);
    trackPromise(
      caAPI.agenteIa.atualizarVinculoBaseConhecimento(idAgenteIa, base.id_base_conhecimento, { ativo })
        .then(() => setBases(atuais => atuais.map(item => (
          item.id_base_conhecimento === base.id_base_conhecimento ? { ...item, vinculo_ativo: ativo } : item
        ))))
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao atualizar vínculo da base de conhecimento.'),
          icon: 'error',
        }))
    );
  }

  function handleMover(indice, deslocamento) {
    const destino = indice + deslocamento;
    if (destino < 0 || destino >= bases.length) return;
    const novaOrdem = [...bases];
    [novaOrdem[indice], novaOrdem[destino]] = [novaOrdem[destino], novaOrdem[indice]];
    trackPromise(
      caAPI.agenteIa.reordenarBasesConhecimento(idAgenteIa, novaOrdem.map(base => base.id_base_conhecimento))
        .then(resposta => setBases(extrairDados(resposta) || novaOrdem))
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao reordenar bases de conhecimento.'),
          icon: 'error',
        }))
    );
  }

  return (
    <div className="bg-white rounded p-3">
      {opcoesDisponiveis.length > 0 && (
        <Form.Group controlId="nova-base">
          <Form.Label>Adicionar base de conhecimento</Form.Label>
          <div className="d-flex">
            <Select
              className="flex-grow-1 mr-2"
              inputId="nova-base"
              options={opcoesDisponiveis.map(base => ({ value: base.id_base_conhecimento, label: base.nome }))}
              value={baseSelecionada}
              onChange={setBaseSelecionada}
              isSearchable
              placeholder="Selecione"
              noOptionsMessage={() => 'Nenhuma base encontrada.'}
            />
            <Button type="button" variant="success" aria-label="Adicionar base de conhecimento" onClick={() => {
              if (baseSelecionada?.value) handleAdicionar(baseSelecionada.value);
            }}>
              <FontAwesomeIcon icon={faPlusCircle} />
            </Button>
          </div>
        </Form.Group>
      )}
      {bases.length === 0 && <p className="text-muted">Nenhuma Base de Conhecimento cadastrada.</p>}
      <ListGroup className="mb-3">
        {bases.map((base, indice) => (
          <ListGroup.Item key={base.id_base_conhecimento} className="d-flex justify-content-between align-items-center">
            <span>
              <span className="text-muted mr-2">{indice + 1}º</span>
              {base.nome}
              {base.ativo === false && <span className="badge badge-secondary ml-2">Base inativa</span>}
            </span>
            <span className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id={`vinculo-ativo-base-${base.id_base_conhecimento}`}
                label="Ativa"
                className="mr-3"
                checked={base.vinculo_ativo !== false}
                onChange={() => handleAlternarVinculo(base)}
              />
              <Button size="sm" variant="outline-secondary" type="button" className="mr-1" aria-label={`Subir ${base.nome}`} disabled={indice === 0} onClick={() => handleMover(indice, -1)}>
                <FontAwesomeIcon icon={faArrowUp} />
              </Button>
              <Button size="sm" variant="outline-secondary" type="button" className="mr-2" aria-label={`Descer ${base.nome}`} disabled={indice === bases.length - 1} onClick={() => handleMover(indice, 1)}>
                <FontAwesomeIcon icon={faArrowDown} />
              </Button>
              <Button size="sm" variant="outline-danger" type="button" aria-label={`Remover ${base.nome}`} onClick={() => handleRemover(base.id_base_conhecimento)}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </span>
          </ListGroup.Item>
        ))}
      </ListGroup>
    </div>
  );
}

// ─── Aba Contatos de Origem ──────────────────────────────────────────────────

function AbaContatosOrigem({ idAgenteIa, ativoAgente, ativa }) {
  const [contatos, setContatos] = useState([]);
  const [elegiveis, setElegiveis] = useState([]);
  const [contatoSelecionado, setContatoSelecionado] = useState(null);
  const [carregado, setCarregado] = useState(false);

  // Reseta ao trocar de agente sem fechar o modal
  useEffect(() => { setCarregado(false); }, [idAgenteIa]);

  useEffect(() => {
    if (!ativa || !idAgenteIa || carregado) return;
    trackPromise(
      Promise.all([
        caAPI.agenteIa.listarContatosOrigem(idAgenteIa),
        caAPI.meio_comunicacao.buscarContatoOrigem('whatsapp'),
      ])
        .then(([resVinculados, reTodos]) => {
          const vinculados = extrairDados(resVinculados) || [];
          const todos = extrairDados(reTodos) || [];
          setContatos(vinculados);
          setElegiveis(todos);
          setCarregado(true);
        })
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao carregar contatos de origem.'),
          icon: 'error',
        }))
    );
  }, [ativa, idAgenteIa, carregado]);

  const idsVinculados = contatos.map(c => c.id_contato_origem);
  const opcoesDisponiveis = elegiveis.filter(c =>
    !idsVinculados.includes(c.id_contato_origem) && !c.id_agente_ia
  );

  function handleVincular(idContato) {
    let requisicao;
    try {
      requisicao = caAPI.contato_origem.atualizarAgenteIa(idContato, idAgenteIa);
    } catch (erro) {
      Swal.fire({ titleText: 'Erro', text: 'Erro inesperado ao vincular contato.', icon: 'error' });
      return;
    }
    const promise = requisicao
      .then(() => {
        const novoContato = elegiveis.find(c => c.id_contato_origem === idContato);
        if (novoContato) setContatos(atuais => [...atuais, novoContato]);
        setContatoSelecionado(null);
      })
      .catch(erro => Swal.fire({
        titleText: 'Erro',
        text: obterMensagemErro(erro, 'Erro ao vincular contato.'),
        icon: 'error',
      }));
    trackPromise(promise);
  }

  function handleDesvincular(contato) {
    Swal.fire({
      titleText: 'Desvincular contato?',
      text: `Remover o vínculo de "${contato.descricao || contato.valor || contato.ramal}" com este Agente?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, desvincular',
      cancelButtonText: 'Cancelar',
    }).then(resultado => {
      if (!resultado.isConfirmed) return;
      let requisicao;
      try {
        requisicao = caAPI.contato_origem.atualizarAgenteIa(contato.id_contato_origem, null);
      } catch (erro) {
        Swal.fire({ titleText: 'Erro', text: 'Erro inesperado ao desvincular contato.', icon: 'error' });
        return;
      }
      const promise = requisicao
        .then(() => setContatos(atual => atual.filter(c => c.id_contato_origem !== contato.id_contato_origem)))
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao desvincular contato.'),
          icon: 'error',
        }));
      trackPromise(promise);
    });
  }

  return (
    <div className="bg-white rounded p-3">
      {!ativoAgente && (
        <Alert variant="warning">
          Este agente está inativo. Ative-o para vincular contatos de origem.
        </Alert>
      )}
      {opcoesDisponiveis.length > 0 && (
        <Form.Group controlId="novo-contato-origem">
          <Form.Label>Vincular contato de origem</Form.Label>
          <div className="d-flex">
            <Select
              className="flex-grow-1 mr-2"
              inputId="novo-contato-origem"
              options={opcoesDisponiveis.map(c => ({
                value: c.id_contato_origem,
                label: c.descricao || c.valor || c.ramal || `#${c.id_contato_origem}`,
              }))}
              value={contatoSelecionado}
              onChange={setContatoSelecionado}
              isSearchable
              isDisabled={!ativoAgente}
              placeholder="Selecione"
              noOptionsMessage={() => 'Nenhum contato encontrado.'}
            />
            <Button type="button" variant="success" aria-label="Vincular contato" disabled={!ativoAgente} onClick={() => {
              if (contatoSelecionado?.value) handleVincular(contatoSelecionado.value);
            }}>
              <FontAwesomeIcon icon={faPlusCircle} />
            </Button>
          </div>
        </Form.Group>
      )}
      {contatos.length === 0 && <p className="text-muted">Nenhum Contato de Origem vinculado.</p>}
      <ListGroup>
        {contatos.map(contato => {
          const label = contato.descricao || contato.valor || contato.ramal || `#${contato.id_contato_origem}`;
          const temPrioridade = Boolean(contato.twilio_ai_assistant_sid && contato.id_agente_ia);
          return (
            <ListGroup.Item key={contato.id_contato_origem} className="d-flex justify-content-between align-items-start">
              <div>
                <div>{label}</div>
                {temPrioridade && (
                  <Alert variant="info" className="mb-0 mt-1 py-1 px-2 small">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    O Agente Dommus tem prioridade no fluxo de atendimento sobre a integração Twilio.
                  </Alert>
                )}
              </div>
              <Button size="sm" variant="outline-danger" type="button" className="ml-2" onClick={() => handleDesvincular(contato)}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    </div>
  );
}

// ─── Aba Fluxo de Atendimento ────────────────────────────────────────────────

function AbaFluxoAtendimento({ idAgenteIa, ativa }) {
  const [etapas, setEtapas] = useState([]);
  const [disponiveis, setDisponiveis] = useState([]);
  const [usandoPadrao, setUsandoPadrao] = useState(false);
  const [etapaSelecionada, setEtapaSelecionada] = useState(null);
  const [carregado, setCarregado] = useState(false);

  // Reseta ao trocar de agente sem fechar o modal.
  useEffect(() => { setCarregado(false); }, [idAgenteIa]);

  // Lazy-load: só carrega quando a aba está ativa, há agente e ainda não carregou.
  // A aba independe de o Agente estar ativo ou inativo (o fluxo é configuração,
  // não elegibilidade operacional).
  useEffect(() => {
    if (!ativa || !idAgenteIa || carregado) return;
    trackPromise(
      caAPI.agenteIa.listarFluxoAtendimento(idAgenteIa)
        .then(resposta => {
          aplicarResposta(extrairDados(resposta));
          setCarregado(true);
        })
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao carregar o fluxo de atendimento.'),
          icon: 'error',
        }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativa, idAgenteIa, carregado]);

  function aplicarResposta(corpo) {
    setEtapas(Array.isArray(corpo?.etapas) ? corpo.etapas : []);
    setDisponiveis(Array.isArray(corpo?.disponiveis) ? corpo.disponiveis : []);
    setUsandoPadrao(Boolean(corpo?.usando_padrao));
  }

  // Salvamento imediato: um único PUT com a lista completa. Em falha, reverte para
  // o estado anterior (rollback otimista) e exibe o erro.
  function salvarFluxo(novasEtapas, estadoAnterior) {
    const payload = novasEtapas.map(etapa => etapa.slug);
    const promise = caAPI.agenteIa.salvarFluxoAtendimento(idAgenteIa, payload)
      .then(resposta => {
        aplicarResposta(extrairDados(resposta));
        successToast.fire({ text: 'Fluxo de atendimento salvo.' });
      })
      .catch(erro => {
        // Rollback otimista para o estado anterior.
        setEtapas(estadoAnterior.etapas);
        setDisponiveis(estadoAnterior.disponiveis);
        setUsandoPadrao(estadoAnterior.usandoPadrao);
        Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao salvar o fluxo de atendimento.'),
          icon: 'error',
        });
      });
    trackPromise(promise);
  }

  function estadoAtual() {
    return { etapas, disponiveis, usandoPadrao };
  }

  function handleReordenar(novasEtapas) {
    const anterior = estadoAtual();
    setEtapas(novasEtapas); // atualização local imediata (otimista)
    salvarFluxo(novasEtapas, anterior);
  }

  function handleAdicionar() {
    if (!etapaSelecionada?.value) return;
    const nova = disponiveis.find(d => d.slug === etapaSelecionada.value);
    if (!nova) return;

    const anterior = estadoAtual();
    const novasEtapas = [...etapas, {
      slug: nova.slug,
      rotulo: nova.rotulo,
      obrigatoria: nova.obrigatoria,
      origem: nova.origem,
      disponivel: true,
    }];
    setEtapas(novasEtapas);
    setEtapaSelecionada(null);
    salvarFluxo(novasEtapas, anterior);
  }

  function handleRemover(slug) {
    const naoObrigatorias = etapas.filter(e => !e.obrigatoria);
    if (naoObrigatorias.length <= 1 && naoObrigatorias.some(e => e.slug === slug)) {
      Swal.fire({
        titleText: 'Não é possível remover',
        text: 'O fluxo não pode ficar sem etapas. Mantenha ao menos uma etapa não obrigatória.',
        icon: 'warning',
      });
      return;
    }

    const anterior = estadoAtual();
    const novasEtapas = etapas.filter(e => e.slug !== slug);
    setEtapas(novasEtapas);
    salvarFluxo(novasEtapas, anterior);
  }

  const temDisponiveis = disponiveis.length > 0;

  return (
    <div className="bg-white rounded p-3">
      {usandoPadrao && (
        <Alert variant="info">
          Este Agente está usando o fluxo padrão do Modelo. Qualquer alteração aqui passa a valer como fluxo personalizado.
        </Alert>
      )}

      {temDisponiveis && (
        <Form.Group controlId="nova-etapa-fluxo">
          <Form.Label>Adicionar etapa</Form.Label>
          <div className="d-flex">
            <Select
              className="flex-grow-1 mr-2"
              inputId="nova-etapa-fluxo"
              options={disponiveis.map(etapa => ({ value: etapa.slug, label: etapa.rotulo || etapa.slug }))}
              value={etapaSelecionada}
              onChange={setEtapaSelecionada}
              isSearchable
              placeholder="Selecione"
              noOptionsMessage={() => 'Nenhuma etapa disponível.'}
            />
            <Button type="button" variant="success" aria-label="Adicionar etapa" onClick={handleAdicionar}>
              <FontAwesomeIcon icon={faPlusCircle} />
            </Button>
          </div>
        </Form.Group>
      )}

      {etapas.length === 0 && <p className="text-muted">Nenhuma etapa no fluxo.</p>}

      <DommusSortable
        itens={etapas}
        extrairId={(etapa) => etapa.slug}
        onReordenar={handleReordenar}
        aria-label="Etapas do fluxo de atendimento"
      >
        {(etapa, _id, indice) => (
          <DommusSortableItem key={etapa.slug} id={etapa.slug}>
            {(alca) => (
              <ListGroup.Item className="d-flex justify-content-between align-items-center mb-1">
                <span className="d-flex align-items-center">
                  <button
                    type="button"
                    ref={alca.ref}
                    {...alca}
                    className="btn btn-link text-muted p-0 mr-2"
                    aria-label={`Arrastar etapa ${etapa.rotulo || etapa.slug}`}
                    style={{ cursor: 'grab' }}
                  >
                    <FontAwesomeIcon icon={faGripVertical} />
                  </button>
                  <Badge variant="primary" className="mr-2">{indice + 1}</Badge>
                  {etapa.rotulo || etapa.slug}
                  {etapa.obrigatoria && <Badge variant="secondary" className="ml-2">obrigatória</Badge>}
                  {etapa.disponivel === false && (
                    <Badge variant="warning" className="ml-2">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />indisponível
                    </Badge>
                  )}
                </span>
                {!etapa.obrigatoria && (
                  <Button size="sm" variant="outline-danger" type="button" aria-label={`Remover etapa ${etapa.rotulo || etapa.slug}`} onClick={() => handleRemover(etapa.slug)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </ListGroup.Item>
            )}
          </DommusSortableItem>
        )}
      </DommusSortable>
    </div>
  );
}

export function ModalRotacaoChaveAgenteIa({ aberto, agente, close, onRotacionada }) {
  const { register, handleSubmit, reset, setError, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { id_chave_api: '', chave_api: '' },
  });

  function handleFechar() {
    reset();
    close();
  }

  function handleRotacionar(valores) {
    return trackPromise(
      caAPI.agenteIa.rotacionarChave(agente.id_agente_ia, valores.chave_api, valores.id_chave_api)
        .then(resposta => {
          const agenteAtualizado = extrairDados(resposta);
          const novoIdChaveApi = agenteAtualizado.id_chave_api || valores.id_chave_api;
          successToast.fire({ text: 'Chave do Agente rotacionada com sucesso.' });
          onRotacionada(agente.id_agente_ia, novoIdChaveApi);
          handleFechar();
        })
        .catch(erro => {
          if (erro?.response?.status === 422) {
            mapearErros422(erro, setError);
            return;
          }
          Swal.fire({
            titleText: 'Erro',
            text: obterMensagemErro(erro, 'Erro ao rotacionar chave do agente.'),
            icon: 'error',
          });
        })
    );
  }

  return (
    <ModalDommus open={aberto} close={handleFechar} titulo="Rotacionar chave">
      <Form onSubmit={handleSubmit(handleRotacionar)}>
        <Form.Group controlId="novo-id-chave-api">
          <Form.Label>Novo identificador público da chave <DommusRequiredSpan /></Form.Label>
          <Form.Control
            {...register('id_chave_api', { required: 'Informe o identificador público da chave.' })}
            isInvalid={Boolean(errors.id_chave_api)}
          />
          {errors.id_chave_api && <Form.Text className="text-danger">{errors.id_chave_api.message}</Form.Text>}
        </Form.Group>
        <Form.Group controlId="nova-chave-api">
          <Form.Label>Nova chave de API <DommusRequiredSpan /></Form.Label>
          <Form.Control
            {...register('chave_api', { required: 'Informe a nova chave de API.' })}
            type="password"
            autoComplete="new-password"
            isInvalid={Boolean(errors.chave_api)}
          />
          {errors.chave_api && <Form.Text className="text-danger">{errors.chave_api.message}</Form.Text>}
        </Form.Group>
        <div className="d-flex justify-content-end">
          <Button type="button" variant="secondary" className="mr-2" onClick={handleFechar}>Cancelar</Button>
          <Button type="submit" variant="warning" disabled={isSubmitting}>Confirmar rotação</Button>
        </div>
      </Form>
    </ModalDommus>
  );
}
